import { SchoolId, TeamId } from '@suntex.fim/api-client';
import { EntityLevel, LoginType } from '@suntex.fim/enums';

import { AuthContext, AuthError, AuthState } from '../types';

import { useAuthContext } from './context';

/**
 * Custom hook for accessing authentication state and actions from `AuthContext`, which can optionally assert authentication requirements via type-constraining predicates on `AuthState`.
 * The hook will throw an AuthError if any of the assertions fail; otherwise, it will return the AuthContext typed to the intersection of the `AuthContext` and the asserted state type.
 * Simply put, this allows you to narrow the type of the authentication state, in order to avoid unnecessary conditionals in your components.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { login } = useAuth(isAuthenticated);
 *   // `login` is now typed as non-nullable
 *   ...
 * }
 * ```
 *
 * Note: this function signature can be expanded to support more assertions as needed.
 */
export function useAuth(): AuthContext;
export function useAuth<A extends AuthState, B extends A>(
  a: AuthAssertion<A, B>,
): B & AuthContext;
export function useAuth<A extends AuthState, B extends A, C extends B>(
  a: AuthAssertion<A, B>,
  b: AuthAssertion<B, C>,
): C & AuthContext;
export function useAuth<
  A extends AuthState,
  B extends A,
  C extends B,
  D extends C,
>(
  a: AuthAssertion<A, B>,
  b: AuthAssertion<B, C>,
  c: AuthAssertion<C, D>,
): D & AuthContext;
export function useAuth(
  ...requirements: readonly AuthAssertion<AuthState, AuthState>[]
): AuthContext {
  const authContext = useAuthContext();

  requirements.forEach((assertion) => {
    if (!assertion(authContext)) {
      throw new AuthError(
        'Insufficient authorization for requirement: ' + assertion.name,
      );
    }
  });

  return authContext;
}

export type AuthAssertion<A extends AuthState, B extends A> = {
  name: string;
} & ((authState: A) => authState is B);

/**
 * Checks if the user is authenticated with complete authentication data.
 *
 * Note: This function returns true during sign-out (when signingOut is true)
 * even though authentication data may be incomplete. This is intentional to
 * prevent spurious AuthError exceptions when components re-render during the
 * sign-out process. Code that relies on isAuthenticated should be aware that
 * it may return true during sign-out with incomplete data.
 */
export function isAuthenticated(
  authState: AuthState,
): authState is AuthState & {
  isAuthenticated: true;
  jwt: NonNullable<AuthState['jwt']>;
  login: NonNullable<AuthState['login']>;
} {
  // Allow the assertion to pass during sign out to prevent spurious auth errors
  // when components re-render during the sign-out process
  if (authState.signingOut) {
    return true;
  }

  if (!authState.isAuthenticated) {
    return false;
  }

  const { id, loginType, entityId, entityLevel } = authState.login ?? {};
  /**
   * Normally, we'd just trust the type system, but since we're dealing with authentication,
   * it's worth being extra careful that our state isn't malformed.
   */
  if (
    id === undefined ||
    loginType === undefined ||
    entityId === undefined ||
    entityLevel === undefined
  ) {
    return false;
  }

  return true;
}

/**
 * Checks if the user is a team leader
 */
export function isTeamLeader(authState: AuthState): authState is AuthState & {
  login: {
    loginType: LoginType.TeamLeader;
    entityId: TeamId;
    entityLevel: typeof EntityLevel.Team;
  };
} {
  if (authState.login?.loginType !== LoginType.TeamLeader) {
    return false;
  }
  if (authState.login.entityLevel !== EntityLevel.Team) {
    throw new Error(
      `Auth state invariant violation: loginType is TeamLeader but entityLevel is ${authState.login.entityLevel}`,
    );
  }
  return true;
}

/**
 * Checks if the user is a school principal
 */
export function isSchoolPrincipal(
  authState: AuthState,
): authState is AuthState & {
  login: {
    loginType: LoginType.SchoolPrincipal;
    entityId: SchoolId;
    entityLevel: typeof EntityLevel.School;
  };
} {
  if (authState.login?.loginType !== LoginType.SchoolPrincipal) {
    return false;
  }
  if (authState.login.entityLevel !== EntityLevel.School) {
    throw new Error(
      `Auth state invariant violation: loginType is SchoolPrincipal but entityLevel is ${authState.login.entityLevel}`,
    );
  }
  return true;
}

/**
 * Checks if the user is a district admin
 */
export function isDistrictAdmin(
  authState: AuthState,
): authState is AuthState & {
  login: {
    loginType: LoginType.DistrictAdmin;
    entityId: string;
    entityLevel: typeof EntityLevel.District;
  };
} {
  if (authState.login?.loginType !== LoginType.DistrictAdmin) {
    return false;
  }
  if (authState.login.entityLevel !== EntityLevel.District) {
    throw new Error(
      `Auth state invariant violation: loginType is DistrictAdmin but entityLevel is ${authState.login.entityLevel}`,
    );
  }
  return true;
}

/**
 * Checks if the user is a region admin
 */
export function isRegionAdmin(authState: AuthState): authState is AuthState & {
  login: {
    loginType: LoginType.RegionAdmin;
    entityLevel: typeof EntityLevel.Region;
  };
} {
  if (authState.login?.loginType !== LoginType.RegionAdmin) {
    return false;
  }
  if (authState.login.entityLevel !== EntityLevel.Region) {
    throw new Error(
      `Auth state invariant violation: loginType is RegionAdmin but entityLevel is ${authState.login.entityLevel}`,
    );
  }
  return true;
}
